import React, { forwardRef } from 'react';
import {
  ComponentSize,
  InputVariant,
  ACCESSIBILITY,
  ERROR_MESSAGES,
} from '../../styles/constants';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  size?: ComponentSize;
  label?: string;
  error?: string;
  helper?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  loading?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      size = 'md',
      label,
      error,
      helper,
      leftIcon,
      rightIcon,
      fullWidth = false,
      loading = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const helperId = helper ? `${inputId}-helper` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    const baseClasses = `
    block rounded-md border transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:opacity-60 disabled:cursor-not-allowed
    placeholder:text-secondary-400
    ${fullWidth ? 'w-full' : ''}
    ${leftIcon ? 'pl-10' : ''}
    ${rightIcon ? 'pr-10' : ''}
    ${loading ? 'cursor-wait' : ''}
  `;

    const sizeClasses = {
      xs: 'px-2 py-1 text-xs min-h-[1.5rem]',
      sm: 'px-3 py-1.5 text-sm min-h-[2rem]',
      md: 'px-3 py-2 text-sm min-h-[2.5rem]',
      lg: 'px-4 py-3 text-base min-h-[3rem]',
      xl: 'px-6 py-4 text-lg min-h-[3.5rem]',
    };

    const variantClasses = {
      default: `
      border-secondary-300 bg-white
      focus:border-primary-500 focus:ring-primary-500
      hover:border-secondary-400
    `,
      filled: `
      border-transparent bg-secondary-100
      focus:border-primary-500 focus:ring-primary-500 focus:bg-white
      hover:bg-secondary-200
    `,
      outline: `
      border-2 border-secondary-300 bg-transparent
      focus:border-primary-500 focus:ring-primary-500
      hover:border-secondary-400
    `,
    };

    const errorClasses = error
      ? `
    border-error-500 focus:border-error-500 focus:ring-error-500
  `
      : '';

    const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${errorClasses}
    ${className}
  `;

    const iconSizeClasses = {
      xs: 'h-3 w-3',
      sm: 'h-4 w-4',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
      xl: 'h-6 w-6',
    };

    const iconPositionClasses = {
      left: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400',
      right:
        'absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400',
    };

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-secondary-700 mb-1"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className={`${iconPositionClasses.left}`}>
              <span className={iconSizeClasses[size]}>{leftIcon}</span>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={combinedClasses}
            aria-describedby={helperId || errorId}
            aria-invalid={!!error}
            {...props}
          />

          {rightIcon && !loading && (
            <div className={iconPositionClasses.right}>
              <span className={iconSizeClasses[size]}>{rightIcon}</span>
            </div>
          )}

          {loading && (
            <div className={iconPositionClasses.right}>
              <svg
                className={`animate-spin ${iconSizeClasses[size]}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} className="mt-1 text-sm text-error-600" role="alert">
            {error}
          </p>
        )}

        {helper && !error && (
          <p id={helperId} className="mt-1 text-sm text-secondary-500">
            {helper}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
